import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '@/lib/api';
import { StickyNote, Check, Loader2 } from 'lucide-react';
import logger from '@/lib/logger';

const MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    [{ color: [] }],
    ['clean'],
  ],
};

const FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'link', 'blockquote', 'color',
];

export default function Scratchpad() {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    api.getScratchpad()
      .then(r => setContent(r.data.content || ''))
      .catch(err => logger.error(err))
      .finally(() => setLoaded(true));
  }, []);

  const debouncedSave = useCallback((val) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      setSaved(false);
      try {
        await api.saveScratchpad(val);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch (err) {
        logger.error(err);
      }
      setSaving(false);
    }, 1200);
  }, []);

  const handleChange = (val) => {
    setContent(val);
    if (loaded) debouncedSave(val);
  };

  if (!loaded) return (
    <div className="border border-slate-200 rounded-sm bg-white p-8 flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
    </div>
  );

  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid="scratchpad-section">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote className="w-4 h-4 text-[#0047AB]" />
          <h2 className="font-heading font-semibold text-slate-900 text-sm">My Notes</h2>
        </div>
        <div className="flex items-center gap-1.5 h-5">
          {saving && (
            <span className="flex items-center gap-1 text-[10px] text-slate-400">
              <Loader2 className="w-3 h-3 animate-spin" /> Saving...
            </span>
          )}
          {saved && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-600" data-testid="scratchpad-saved-indicator">
              <Check className="w-3 h-3" /> Saved
            </span>
          )}
        </div>
      </div>
      <div className="scratchpad-editor" data-testid="scratchpad-editor">
        <ReactQuill
          theme="snow"
          value={content}
          onChange={handleChange}
          modules={MODULES}
          formats={FORMATS}
          placeholder="Write your personal notes here..."
        />
      </div>
    </div>
  );
}
